import type { PortalConfig, ProgressSummary, SectionId } from './config'
import { resolvePlatform } from './config'
import { isComplete, isEmpty, needsReview, type Field } from './field'
import { contactLabel } from './contacts'
import { accountId } from './ids'
import { PLATFORMS, PLATFORM_KEYS, type PlatformKey } from './platforms'
import { SECTIONS, type FieldKind } from './registry'
import { FAQ_CATALOG } from './catalogs'
import type { SectionDocs } from './sections'

/**
 * `name` reads as "Colony Square · Hours": where the field is (`where`), then which field (`label`). `byClient`
 * marks a step only the client can take (granting access), which staff prefill does not count. `questionId` is set
 * on FAQ answers so a screen can show the question's current wording from the shared catalog; `detail` names the
 * location when a brand's answer differs by location.
 */
export type Leaf = {
  path: string
  field: Field<unknown> | undefined
  required: boolean
  confirmable: boolean
  name: string
  where?: string
  label: string
  kind: FieldKind
  byClient: boolean
  questionId?: string
  detail?: string
  /** On the Platforms step: whose handle or access this is, so a screen can show the full address. */
  platform?: PlatformKey
}

type At = { where?: string; label: string; kind: FieldKind; questionId?: string; detail?: string; platform?: PlatformKey }

/** Expand the registry into concrete leaves for one section of one client. */
export function sectionLeaves(id: SectionId, config: PortalConfig, docs: Partial<SectionDocs>): Leaf[] {
  const def = SECTIONS[id]
  const out: Leaf[] = []
  const push = (path: string, field: Field<unknown> | undefined, required: boolean, confirmable = true, at: At = { label: path, kind: 'text' }, byClient = false) =>
    out.push({ path, field, required, confirmable, name: [at.where, at.label, at.detail].filter(Boolean).join(' · '), ...at, byClient })
  const asField = (v: unknown) => v as Field<unknown> | undefined
  const named = (where: string | undefined, label: string, kind: FieldKind = 'text'): At => ({ where, label, kind })
  const brandName = (bid: string) => docs.brands?.items[bid]?.name?.value?.trim() || 'Untitled brand'

  switch (id) {
    case 'brands': {
      const doc = docs.brands
      for (const bid of doc?.order ?? []) {
        const item = doc?.items[bid]
        for (const f of def.fields) push(`brands.items.${bid}.${f.key}`, asField(item?.[f.key as keyof typeof item]), f.required, f.confirmable ?? true, named(brandName(bid), f.label, f.kind))
      }
      break
    }
    case 'audience': {
      for (const bid of docs.brands?.order ?? []) {
        const item = docs.audience?.items[bid]
        for (const f of def.fields) push(`audience.items.${bid}.${f.key}`, asField(item?.[f.key as keyof typeof item]), f.required, f.confirmable ?? true, named(brandName(bid), f.label, f.kind))
      }
      break
    }
    case 'locations': {
      const doc = docs.locations
      for (const lid of doc?.order ?? []) {
        const item = doc?.items[lid]
        const where = item?.name?.value?.trim() || 'Untitled location'
        // A location that is not open yet needs its expected opening instead of hours.
        const upcoming = Boolean(item?.upcoming)
        for (const f of def.fields) {
          // The main line now lives in Contacts; an older phone here is hidden, so it is not asked about.
          if (f.key === 'phone') continue
          if (f.key === 'opening' && !upcoming) continue
          const required = f.key === 'opening' ? true : f.key === 'schedules' ? f.required && !upcoming : f.required
          push(`locations.items.${lid}.${f.key}`, asField(item?.[f.key as keyof typeof item]), required, f.confirmable ?? true, named(where, f.label, f.kind))
        }
      }
      break
    }
    case 'social': {
      // Every card the page shows, in page order, whether or not anyone has typed into it yet.
      for (const bid of docs.brands?.order ?? []) {
        for (const platform of PLATFORM_KEYS) {
          const setting = resolvePlatform(config, bid, platform)
          if (!setting.include) continue
          const aid = accountId(bid, platform)
          const item = docs.social?.items[aid]
          const p = PLATFORMS[platform]
          for (const f of def.fields) {
            // Only what the card shows: access and the login are asked for managed platforms, notes never.
            if (f.key === 'notes') continue
            if ((f.key === 'status' || f.key === 'login') && !setting.managed) continue
            if (f.key === 'login' && !p.credentials) continue
            const required = f.required || (Boolean(f.requiredWhenManaged) && setting.managed)
            push(`social.items.${aid}.${f.key}`, asField(item?.[f.key as keyof typeof item]), required, f.confirmable ?? true, { ...named(`${brandName(bid)} · ${p.name}`, f.key === 'handle' ? p.handleLabel : f.label, f.kind), platform }, Boolean(f.clientProvides))
          }
        }
      }
      break
    }
    case 'assets': {
      for (const [did, d] of Object.entries(docs.assets?.drives ?? {})) {
        for (const f of def.fields) push(`assets.drives.${did}.${f.key}`, asField(d[f.key as keyof typeof d]), f.required, true, named(d.label?.value?.trim() || 'Folder', f.label, f.kind))
      }
      break
    }
    case 'links': {
      for (const bid of docs.brands?.order ?? []) {
        const links = docs.links?.keyLinks[bid]
        for (const f of def.fields) push(`links.keyLinks.${bid}.${f.key}`, asField(links?.[f.key as keyof typeof links]), f.required, true, named(brandName(bid), f.label, f.kind))
      }
      break
    }
    case 'specials': {
      for (const lid of docs.locations?.order ?? []) {
        const promos = docs.specials?.promotions?.[lid]
        if (promos) push(`specials.promotions.${lid}`, asField(promos), false, true, named(docs.locations?.items[lid]?.name?.value?.trim() || 'Untitled location', 'Specials & promotions', 'promotions'))
      }
      push('specials.events', asField(docs.specials?.events), false, true, named(undefined, 'Events & milestones', 'textarea'))
      break
    }
    case 'contacts': {
      const items = Object.entries(docs.contacts?.items ?? {}).sort(([, a], [, b]) => a.createdAt - b.createdAt)
      for (const [cid, c] of items) {
        for (const f of def.fields) push(`contacts.items.${cid}.${f.key}`, asField(c[f.key as 'name' | 'role' | 'phone' | 'email']), false, true, named(contactLabel(c), f.label, f.kind))
      }
      break
    }
    case 'faq': {
      for (const bid of docs.brands?.order ?? []) {
        const brandLocations = (docs.locations?.order ?? []).filter((lid) => docs.locations?.items[lid]?.brandId === bid)
        for (const [qid, a] of Object.entries(docs.faq?.brands?.[bid] ?? {})) {
          const label = docs.faq?.custom?.[qid]?.label ?? docs.faq?.labels?.[qid] ?? FAQ_CATALOG.find((q) => q.id === qid)?.label ?? qid
          const at = (detail?: string): At => ({ where: brandName(bid), label, kind: 'textarea', questionId: qid, detail })
          if (a.split) for (const lid of brandLocations) push(`faq.brands.${bid}.${qid}.locations.${lid}`, asField(a.locations?.[lid]), false, true, at(docs.locations?.items[lid]?.name?.value?.trim() || 'Untitled location'))
          else push(`faq.brands.${bid}.${qid}.answer`, asField(a.answer), false, true, at())
        }
      }
      break
    }
  }
  return out
}

export type MissingField = {
  /** Path inside the section's document, as the section editor and its fields' `data-field` use it. */
  path: string
  name: string
}

export type PrepSection = { filled: number; total: number; missing: MissingField[] }

/**
 * Staff prefill progress: the required fields Sonder fills in, answered by anyone. Steps only the client can
 * take (granting access) are left out. Sections with no required fields count as one unit, as for the client.
 * `missing` lists the empty ones in page order, for staff to jump to.
 */
export function prepProgress(config: PortalConfig, docs: Partial<SectionDocs>): { overall: { filled: number; total: number }; sections: Partial<Record<SectionId, PrepSection>> } {
  const sections: Partial<Record<SectionId, PrepSection>> = {}
  let filled = 0
  let total = 0
  for (const s of config.sections) {
    if (!s.enabled) continue
    const def = SECTIONS[s.id]
    const leaves = sectionLeaves(s.id, config, docs)
    const hasValue = (l: Leaf) => Boolean(l.field && !isEmpty(l.field.value))
    let p: PrepSection
    if (def.fields.some((f) => f.required)) {
      const mine = leaves.filter((l) => l.required && !l.byClient)
      p = {
        filled: mine.filter(hasValue).length,
        total: mine.length,
        missing: mine.filter((l) => !hasValue(l)).map((l) => ({ path: l.path.slice(s.id.length + 1), name: l.name })),
      }
    } else {
      p = { filled: leaves.some(hasValue) ? 1 : 0, total: 1, missing: [] }
    }
    // Files are the client's to share, so they never count toward Sonder's total, but once some are
    // uploaded the Assets step shows as done rather than untouched.
    if (s.id === 'assets' && p.total === 0 && Object.keys(docs.assets?.uploads ?? {}).length > 0) {
      sections[s.id] = { filled: 1, total: 1, missing: [] }
      continue
    }
    sections[s.id] = p
    if (!def.optional) {
      filled += p.filled
      total += p.total
    }
  }
  return { overall: { filled, total }, sections }
}

export type SectionProgress = { done: number; total: number; filled: number; unconfirmed: number; state: 'todo' | 'partial' | 'done' | 'review' }

export function sectionProgress(id: SectionId, config: PortalConfig, docs: Partial<SectionDocs>): SectionProgress {
  const def = SECTIONS[id]
  const leaves = sectionLeaves(id, config, docs)
  const required = leaves.filter((l) => l.required)
  const unconfirmed = leaves.filter((l) => l.confirmable && needsReview(l.field)).length
  let done: number
  let total: number
  let filled: number
  const hasValue = (l: Leaf) => Boolean(l.field && !isEmpty(l.field.value))
  const defHasRequired = def.fields.some((f) => f.required)
  if (defHasRequired) {
    total = required.length
    done = required.filter((l) => isComplete(l.field)).length
    filled = required.filter(hasValue).length
    // Assets is the one section the client must add to themselves: with no folders it is one unit,
    // done once any file is uploaded. Other sections have nothing to count until staff compose entities.
    if (total === 0 && id === 'assets') {
      const uploaded = Object.keys(docs.assets?.uploads ?? {}).length > 0 ? 1 : 0
      total = 1
      done = uploaded
      filled = uploaded
    }
  } else {
    // Sections with no required fields (Specials & events, FAQ) count as one unit: anything complete = done.
    total = 1
    done = leaves.some((l) => isComplete(l.field)) ? 1 : 0
    filled = leaves.some(hasValue) ? 1 : 0
  }
  const anyValue = leaves.some(hasValue)
  const state: SectionProgress['state'] =
    unconfirmed > 0 ? 'review' : total === 0 ? 'todo' : done === 0 ? (anyValue ? 'partial' : 'todo') : done < total ? 'partial' : 'done'
  return { done, total, filled, unconfirmed, state }
}

export function computeProgress(config: PortalConfig, docs: Partial<SectionDocs>): ProgressSummary & { states: Record<string, SectionProgress['state']> } {
  const sections: ProgressSummary['sections'] = {}
  const states: Record<string, SectionProgress['state']> = {}
  let done = 0
  let total = 0
  let filled = 0
  for (const s of config.sections) {
    if (!s.enabled) continue
    const p = sectionProgress(s.id, config, docs)
    sections[s.id] = { done: p.done, total: p.total, filled: p.filled, unconfirmed: p.unconfirmed }
    states[s.id] = p.state
    if (!SECTIONS[s.id].optional) {
      done += p.done
      total += p.total
      filled += p.filled
    }
  }
  return { overall: { done, total, filled }, sections, states }
}
